import { Grid } from '@mui/material';
import { ViewTransactionHistoryButton } from '@tassat/button';
import { Statistic } from '@tassat/common-definitions';
import { StatisticListGroup } from '@tassat/statistic';
import React from 'react';

import history from 'Lib/browserHistory';
import { printAssetStatistic, printNumberStatistic } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAllMappedWalletByAccountId } from 'Selectors/accounts';
import { getFirmByFirmId } from 'Selectors/firms';

import FirmWalletDetailsHeader from './firmWalletDetailsHeader';
import FirmWalletUsers from './firmWalletUsers';
import { FIRM_VIEW_WALLETS_TAB } from '../..';

import { FirmWalletDetailsProps } from '.';

export function FirmWalletDetails (props: FirmWalletDetailsProps) {
    const {
        classes,
        match,
        assetPair
    } = props;

    const firmId = match.params.firmId;
    const firm = useAppSelector((state) => getFirmByFirmId(state, firmId));
    const mappedWallet = useAppSelector((state) => getAllMappedWalletByAccountId(state, match.params.accountId));

    const {
        wallet,
        walletStats
    } = mappedWallet;

    React.useEffect(() => {
        if (!wallet) {
            history.push(`/admin/firms/${firmId}`, { tab: FIRM_VIEW_WALLETS_TAB });
        }
    }, [ wallet, firmId ]);

    if (!wallet) return <div />;

    const transactionStatistics = () => {
        const statMap: Array<Statistic> = [];
        walletStats?.forEach((walletAssetStat) => {
            if (walletAssetStat.AssetId === assetPair.currency?.AssetId) {
                statMap.push({
                    title: 'Today\'s USD Sends',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysSendsAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysSendsCount)
                });
                statMap.push({
                    title: 'Today\'s USD Receives',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysReceivesAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysReceivesCount)
                });
                statMap.push({
                    title: 'Today\'s Withdrawals',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysWithdrawalsAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysWithdrawalsCount)
                });
                statMap.push({
                    title: 'Today\'s Deposits',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysDepositsAmount, assetPair.currency, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysDepositsCount)
                });
            }
            if (walletAssetStat.AssetId === assetPair.security?.AssetId) {
                statMap.push({
                    title: 'Today\'s TFND Sends',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysSendsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysSendsCount)
                });
                statMap.push({
                    title: 'Today\'s TFND Receives',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysReceivesAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysReceivesCount)
                });
                statMap.push({
                    title: 'Today\'s Buys',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysBuysAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysBuysCount)
                });
                statMap.push({
                    title: 'Today\'s Sells',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysSellsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysSellsCount)
                });
                statMap.push({
                    title: 'Today\'s Subscriptions',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysSubscriptionsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysSubscriptionsCount)
                });
                statMap.push({
                    title: 'Today\'s Redemptions',
                    statistic: printAssetStatistic(walletAssetStat && walletAssetStat.TodaysRedemptionsAmount, assetPair.security, 'symbol'),
                    statisticCount: printNumberStatistic(walletAssetStat && walletAssetStat.TodaysRedemptionsCount)
                });
            }
        });

        return statMap;
    };

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <FirmWalletDetailsHeader
                firm={firm}
                mappedWallet={mappedWallet}
            />
            <Grid
                className={classes.accountAdditionalInfoContainer}
                container
                direction='row'
                justifyContent='space-evenly'
                spacing={0}
                wrap='wrap'
            >
                <Grid
                    alignItems='center'
                    className={classes.accountStatisticsPanel}
                    container
                    direction='column'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <StatisticListGroup
                        id='firmWalletDetails_transactionStatistics'
                        itemClass={classes.statContainer}
                        statistics={transactionStatistics()}
                    />
                </Grid>
                <Grid
                    className={classes.accountUsersPanel}
                    item
                    xs={7}
                >
                    <FirmWalletUsers
                        firm={firm}
                        match={match}
                        wallet={wallet}
                    />
                </Grid>
                <Grid
                    className={classes.accountTransactionHistoryPanel}
                    item
                >
                    <ViewTransactionHistoryButton
                        account={wallet}
                        history={history}
                        text='View Account Transaction History'
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}